import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, IconButton, Typography, Tooltip } from '@mui/material';
import React, { useMemo, useState, useEffect } from 'react';
const ScrollAwareTooltip = ({ children, ...props }) => {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!open)
            return;
        const handleScroll = () => setOpen(false);
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [open]);
    return (_jsx(Tooltip, { ...props, open: open, onOpen: () => setOpen(true), onClose: () => setOpen(false), children: children }));
};
import { palette } from '../../../../../theme/palette';
import { colCenter } from '@ui/style/style';
import { spaceBetween } from './style';
import { ArrowRight, Edit, Eye, Archive, ArchiveSlash } from 'iconsax-react';
import { ClientDialog } from './dialog/ClientDialog';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { formatDisplayDate } from '@infrastructure/helpers';
import { downloadCOCDocumentAsync } from '@application/features/freelancer/freelancerThunks';
import { openOrDownloadPdf } from '@application/common/helpers/file-helper';
import { ClientContractStatus } from '@application/common/enums/client-contract-status';
import AssignmentApi from '@infrastructure/assignment-api';
import { getUserByIdAsync } from '@application/features/adminUsers/adminUserThunk';
const theme = palette('light');
export const ClientsInformation = ({ showArchived = false }) => {
    const [isOpen, setisOpen] = React.useState(false);
    const [selectedAssignmentId, setSelectedAssignmentId] = useState(undefined);
    const [isViewOnly, setIsViewOnly] = useState(false);
    const handleEdit = (assignmentId, viewOnly = false) => {
        setSelectedAssignmentId(assignmentId);
        setIsViewOnly(viewOnly);
        setisOpen(true);
    };
    const handleClose = () => {
        setisOpen(false);
        setIsViewOnly(false);
    };
    return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsx(CLientCard, { handleEdit: handleEdit, showArchived: showArchived }), _jsx(ClientDialog, { handleClose: handleClose, type: 'edit', open: isOpen, assigmentId: selectedAssignmentId, viewOnly: isViewOnly })] }));
};
const USE_ARCHIVED_FILTER = true;
export const CLientCard = ({ handleEdit, showArchived = false }) => {
    const { user } = useAppSelector((state) => state.user);
    const dispatch = useAppDispatch();
    const [loadingStates, setLoadingStates] = useState({});
    const [archiveLoadingStates, setArchiveLoadingStates] = useState({});
    const sortedAssignments = useMemo(() => {
        return [...(user?.assignments ?? [])]
            .filter((a) => {
            if (!USE_ARCHIVED_FILTER)
                return true;
            return showArchived ? !!a.isArchived : !a.isArchived;
        })
            .sort((a, b) => {
            const aTime = new Date(a.contactStartDate ?? a.contactEndDate ?? 0).getTime();
            const bTime = new Date(b.contactStartDate ?? b.contactEndDate ?? 0).getTime();
            return bTime - aTime;
        });
    }, [user?.assignments, showArchived]);
    const downloadContract = (contract) => {
        const fileDto = { folderName: contract?.folderName, fileName: contract?.name };
        dispatch(downloadCOCDocumentAsync(fileDto))
            .unwrap()
            .then((res) => {
            openOrDownloadPdf(res.file, contract?.name ?? 'contract.pdf', 'Client Contract');
        });
    };
    const setIsLoading = (assignmentId, isLoading) => {
        setLoadingStates((prev) => ({ ...prev, [assignmentId]: isLoading }));
    };
    const getIsLoading = (assignmentId) => {
        return loadingStates[assignmentId] || false;
    };
    const handleArchiveToggle = async (assignmentId, isArchived) => {
        setArchiveLoadingStates((prev) => ({ ...prev, [assignmentId]: true }));
        try {
            await AssignmentApi.updateAssignmentArchiveStatus(assignmentId, isArchived);
            if (user?.id) {
                dispatch(getUserByIdAsync(user.id));
            }
        }
        catch {
        }
        finally {
            setArchiveLoadingStates((prev) => ({ ...prev, [assignmentId]: false }));
        }
    };
    return (_jsx(_Fragment, { children: sortedAssignments?.length ? (sortedAssignments.map((assignment, index) => (_jsx(Box, { mt: 2, sx: {
                background: 'white',
                borderRadius: '16px',
                padding: '20px'
            }, children: _jsxs(Box, { sx: { mb: 2 }, children: [_jsxs(Box, { sx: { ...spaceBetween }, children: [_jsx(Typography, { variant: 'h6', color: 'initial', children: assignment.clientName }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center' }, children: [assignment.status == ClientContractStatus.Expired ? (_jsx(ScrollAwareTooltip, { title: 'View Assignment', children: _jsx(IconButton, { onClick: () => handleEdit(assignment.id, true), children: _jsx(Eye, {}) }) })) : (_jsx(ScrollAwareTooltip, { title: 'Edit Assignment', children: _jsx(IconButton, { onClick: () => handleEdit(assignment.id), children: _jsx(Edit, {}) }) })), !assignment.isArchived ? (_jsx(ScrollAwareTooltip, { title: 'Archive Assignment', children: _jsx(IconButton, { disabled: !!archiveLoadingStates[assignment.id], onClick: () => handleArchiveToggle(assignment.id, true), children: _jsx(Archive, { size: 20 }) }) })) : (_jsx(ScrollAwareTooltip, { title: 'Unarchive Assignment', children: _jsx(IconButton, { disabled: !!archiveLoadingStates[assignment.id], onClick: () => handleArchiveToggle(assignment.id, false), children: _jsx(ArchiveSlash, { size: 20 }) }) }))] })] }), _jsxs(Box, { sx: { ...colCenter, gap: 0.5 }, children: [_jsx(Typography, { variant: 'subtitle2', color: 'initial', children: formatDisplayDate(assignment.contactStartDate) }), _jsx(ArrowRight, { size: '14px' }), _jsx(Typography, { variant: 'subtitle2', color: 'initial', children: formatDisplayDate(assignment.contactEndDate) })] }), assignment.assignmentExtendedDate && formatDisplayDate(assignment.assignmentExtendedDate) && (_jsxs(Typography, { variant: 'subtitle2', color: '#1C9AD8', children: ["Contract Extended to ", formatDisplayDate(assignment.assignmentExtendedDate)] })), assignment.terminationDate && formatDisplayDate(assignment.terminationDate) && (_jsxs(Typography, { variant: 'subtitle2', color: '#FF0000', children: ["Terminated on ", formatDisplayDate(assignment.terminationDate)] })), _jsx(Typography, { variant: 'subtitle2', color: 'initial', children: user?.position }), _jsx(Typography, { variant: 'subtitle2', color: 'initial', children: assignment.contactPersonFullName })] }, index) }, assignment.id ?? index)))) : (_jsx(Typography, { variant: 'body1', color: 'initial', children: "No assignments available." })) }));
};
